import utils from "../universal/utils.js";
import { formatMapName } from "./maps.js";

const externalMap = (display, imageUrl, workshopItemId = null) => ({
  display,
  thumbnailSourceType: "externalUrl",
  imageUrl,
  workshopItemId,
});

const s3Map = (display, mapId, workshopItemId = null) => ({
  display,
  thumbnailSourceType: "s3",
  edgePath: `/api/v1/media/map-thumbnail/${encodeURIComponent(mapId)}`,
  imageUrl: null,
  workshopItemId,
});

// Wingman (2v2) pool — keyed by the real CS2 map name (which is not always
// de_-prefixed). Keep in sync with edge/constants/wingmanMaps.js
// (guarded by edge/test/wingmanMaps.test.js).
export const WINGMAN_MAP_META_BY_ID = {
  de_assembly: s3Map("Assembly", "de_assembly", "3071005299"),
  de_boyard: s3Map("Boyard", "de_boyard", "3228904725"),
  de_brewery: s3Map("Brewery", "de_brewery", "3070290240"),
  cobblewingman: s3Map("Cobblestone Wingman", "cobblewingman", "3645126146"),
  de_drawbridge: s3Map("Drawbridge", "de_drawbridge", "3070192462"),
  de_eldorado: s3Map("Eldorado", "de_eldorado", "3408790618"),
  de_foroglio: s3Map("Foroglio", "de_foroglio", "3132854332"),
  de_lake: s3Map("Lake", "de_lake", "3219506727"),
  de_memento: s3Map("Memento", "de_memento", "3165559377"),
  de_palais: s3Map("Palais", "de_palais", "3257582863"),
  de_rainfall: s3Map("Rainfall", "de_rainfall", "3265650949"),
  gd_rialto: s3Map("Rialto", "gd_rialto", "3085490518"),
  de_splat: s3Map("Splat", "de_splat", "3439120481"),
  de_trash: s3Map("Trash", "de_trash", "3428771893"),
  de_vandal: s3Map("Vandal", "de_vandal", "3071899764"),
};

// Allowlisted map metadata used for safe UI rendering (no server-provided URLs).
// Keyed by the match map id used server-side (e.g. "de_mirage").
export const MAP_META_BY_ID = {
  de_nuke: externalMap("Nuke", "https://primagames.com/wp-content/uploads/2023/10/Nuke-Callouts-for-CS2.jpg?w=1200"),
  de_mirage: externalMap("Mirage", "https://static.wikia.nocookie.net/cswikia/images/f/f5/De_mirage_cs2.png"),
  de_cache: externalMap(
    "Cache",
    "https://images.steamusercontent.com/ugc/33317948943897151/ED394A1B2BECE0841A09CE7DBEC0CEFCDEF092BA/?imw=5000&imh=5000&ima=fit&impolicy=Letterbox&imcolor=%23000000&letterbox=false"
  ),
  de_ancient: externalMap(
    "Ancient",
    "https://prosettings.net/cdn-cgi/image/dpr=1%2Cf=auto%2Cfit=cover%2Cgravity=auto%2Cheight=536%2Cq=85%2Cwidth=970/wp-content/uploads/ancient-1.jpg"
  ),
  de_dust_ii: externalMap("Dust II", "https://www.esports.net/wp-content/uploads/2023/10/Dust-2-CS2-1.jpg"),
  de_vertigo: externalMap("Vertigo", "https://pbs.twimg.com/media/F1RTa4NX0AMOVBl.jpg:large"),
  de_inferno: externalMap("Inferno", "https://tradeit.gg/blog/wp-content/uploads/2023/09/CS2-Inferno-Callouts-8-1024x576.webp"),
  de_anubis: externalMap(
    "Anubis",
    "https://steamuserimages-a.akamaihd.net/ugc/2193876107321240926/4F3A2D9E8A51B390FC2B77407F9CBF0E60F555B9/?imw=5000&imh=5000&ima=fit&impolicy=Letterbox&imcolor=%23000000&letterbox=false"
  ),
  de_cobblestone_2018: s3Map("Cobblestone", "de_cobblestone_2018", "3329387648"),
  ...WINGMAN_MAP_META_BY_ID,
};

export function getMapMetaById(mapId) {
  if (!mapId || typeof mapId !== "string") return null;

  const trimmed = mapId.trim();
  if (!trimmed) return null;

  const direct = MAP_META_BY_ID[trimmed];
  if (direct) return direct;

  const withDePrefix = trimmed.startsWith("de_") ? trimmed : `de_${trimmed}`;
  const prefixed = MAP_META_BY_ID[withDePrefix];
  if (prefixed) return prefixed;

  const withoutDePrefix = trimmed.replace(/^de_/, "");
  return {
    display: formatMapName(withoutDePrefix),
    thumbnailSourceType: "externalUrl",
    imageUrl: null,
    workshopItemId: null,
  };
}

export function getMapWorkshopItemId(mapId) {
  const mapMeta = getMapMetaById(mapId);
  if (!mapMeta?.workshopItemId) return null;
  return String(mapMeta.workshopItemId).trim() || null;
}

export function getMapImageUrl(
  mapId,
  edgeBasePath = utils.getEdgeBasePath()
) {
  const mapMeta = getMapMetaById(mapId);
  if (!mapMeta) return null;

  if (mapMeta.thumbnailSourceType === "s3") {
    return `${edgeBasePath}${mapMeta.edgePath}`;
  }

  return mapMeta.imageUrl || null;
}

